import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const RISK_BADGE = {
  High: 'bg-red-100 text-red-700 border border-red-200',
  Medium: 'bg-amber-100 text-amber-700 border border-amber-200',
  Low: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
};

const STATUS_BADGE = {
  Open: 'bg-red-50 text-red-700',
  'Under review': 'bg-amber-50 text-amber-700',
  Closed: 'bg-emerald-50 text-emerald-700',
};

const NearMissTable = ({ nearMisses, loading, onViewDetail, onDelete }) => {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="h-14 bg-muted/40 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!nearMisses?.length) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertTriangle" size={28} className="text-amber-400" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1">{t('nearMissNoneFound')}</h3>
        <p className="text-sm text-muted-foreground">{t('nearMissNoneFoundDesc')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColDate')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColSite')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColLocation')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColDescription')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColRisk')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColStatus')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColReportedBy')}</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('nearMissColPhotos')}</th>
            <th className="text-right py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {nearMisses?.map(nm => (
            <tr
              key={nm?.id}
              className="hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() => onViewDetail?.(nm)}
            >
              <td className="py-3 px-3 text-foreground whitespace-nowrap">
                {nm?.date ? new Date(nm?.date)?.toLocaleDateString('en-GB') : '—'}
              </td>
              <td className="py-3 px-3 text-foreground whitespace-nowrap">
                {nm?.site?.site_name || '—'}
              </td>
              <td className="py-3 px-3 text-muted-foreground max-w-[120px] truncate">
                {nm?.location || '—'}
              </td>
              <td className="py-3 px-3 max-w-[200px]">
                <span
                  className="text-foreground line-clamp-2 text-xs"
                  title={nm?.description}
                >
                  {nm?.description}
                </span>
              </td>
              <td className="py-3 px-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${RISK_BADGE?.[nm?.risk_level] || 'bg-muted text-muted-foreground'}`}>
                  {nm?.risk_level}
                </span>
              </td>
              <td className="py-3 px-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE?.[nm?.status] || 'bg-muted text-muted-foreground'}`}>
                  {nm?.status}
                </span>
              </td>
              <td className="py-3 px-3 text-muted-foreground text-xs whitespace-nowrap">
                {nm?.reporter?.full_name || '—'}
              </td>
              <td className="py-3 px-3 text-center">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="Image" size={12} />
                  {nm?.photos?.length ?? 0}
                </span>
              </td>
              <td className="py-3 px-3" onClick={e => e?.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => onViewDetail?.(nm)}
                    className="p-1.5 rounded-lg hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                    title={t('viewDetails')}
                  >
                    <Icon name="Eye" size={15} />
                  </button>
                  {/* NSherjFh PR 5: admin Delete (active) or Restore (deleted),
                      mutually exclusive at the page level. */}
                  {onDelete && (
                    <button
                      onClick={() => onDelete?.(nm)}
                      className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      title={t('delete')}
                    >
                      <Icon name="Trash2" size={15} />
                    </button>
                  )}
                  {onRestore && (
                    <button
                      onClick={() => onRestore?.(nm)}
                      className="p-1.5 rounded-lg hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                      title="Restore"
                    >
                      <Icon name="RotateCcw" size={15} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default NearMissTable;
